import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Stack,
  TextField,
  Button,
  Skeleton,
  Alert,
  alpha,
  InputAdornment,
  Menu,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaidIcon from '@mui/icons-material/Paid';
import SaveIcon from '@mui/icons-material/Save';
import LockIcon from '@mui/icons-material/Lock';
import { useNexaStayTheme } from './NexaStayThemeContext';
import { useNexaStayBasePath, nexastayPath, resolveNexaStayHotelId } from './useNexaStayBasePath';
import { getGuestProfile, getGuestStorageKey, updateGuest, getHotelInfo } from './nexastayApi';
import { buildInputSxMinimal } from './nexastayUtils';
import countryCodesData from '../data/country-codes.json';
import countriesData from '../data/countries-states-cities-data';
import { countryFilterOptions } from '../utils/locationSearch';
import {
  formatPhoneLengthLabel,
  normalizePhoneInputForCountry,
  resolveDialCodeForCountryInput,
  resolveCountryNameForInput,
  resolveCountryIsoForInput,
} from '../utils/phoneValidation';
import { resolveGuestPhoneLocaleFromProfile } from './nexastayGuestLocale';

function formatPhoneWithDialCode(phone, countryCode) {
  const digits = String(phone || '').trim();
  if (!digits) return '';
  const dial = String(countryCode || '').trim() || '+91';
  if (digits.startsWith('+')) return digits;
  return `${dial} ${digits}`;
}

export default function ProfilePage() {
  const { hotelId: routeHotelId } = useParams();
  const hotelId = resolveNexaStayHotelId(routeHotelId);
  const location = useLocation();
  const basePath = useNexaStayBasePath();
  const theme = useNexaStayTheme();
  const accent = theme.accent;
  const fromBooking = location.state?.fromBooking === true;

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country_code: '+91',
    address_line1: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
    new_password: '',
  });
  const [phoneCountryCodeMenuOpen, setPhoneCountryCodeMenuOpen] = useState(false);
  const [phoneCountryCodeSearch, setPhoneCountryCodeSearch] = useState('');
  const [countryTouched, setCountryTouched] = useState(false);
  const [phoneCountryCodeTouched, setPhoneCountryCodeTouched] = useState(false);
  const [hotel, setHotel] = useState(null);
  const phoneCountryCodeAnchorRef = useRef(null);
  const profileFormInitializedRef = useRef(false);
  const countryCodes = useMemo(() => countryCodesData, []);
  const guestCountries = useMemo(
    () => (Array.isArray(countriesData?.countries) ? countriesData.countries : []),
    []
  );
  const selectedGuestCountryObj = useMemo(() => {
    if (!guestCountries.length || !String(form.country || '').trim()) return null;
    const raw = String(form.country).trim();
    return (
      guestCountries.find(
        (c) =>
          c?.name === raw ||
          c?.code === raw ||
          (c?.name && c.name.toLowerCase() === raw.toLowerCase())
      ) || null
    );
  }, [guestCountries, form.country]);
  const selectedPhoneCountryCode = useMemo(
    () =>
      countryCodes.find((cc) => cc.dialCode === form.country_code) ||
      countryCodes.find((cc) => cc.code === 'IN') ||
      countryCodes[0],
    [countryCodes, form.country_code]
  );

  useEffect(() => {
    if (!hotelId) {
      setLoading(false);
      setError('Hotel not found.');
      return;
    }
    let cancelled = false;
    const guestId = typeof window !== 'undefined' ? localStorage.getItem(getGuestStorageKey(hotelId)) : null;
    if (!guestId) {
      setLoading(false);
      setError('not_logged_in');
      return;
    }
    getGuestProfile(hotelId, guestId)
      .then((data) => {
        if (!cancelled) setProfile(data);
      })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || e.message || 'Failed to load profile.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [hotelId]);

  useEffect(() => {
    if (!hotelId) return;
    let cancelled = false;
    getHotelInfo(hotelId)
      .then((data) => { if (!cancelled) setHotel(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [hotelId]);

  useEffect(() => {
    profileFormInitializedRef.current = false;
  }, [hotelId]);

  useEffect(() => {
    if (!profile || countryTouched || phoneCountryCodeTouched) return;
    const locale = resolveGuestPhoneLocaleFromProfile(profile, {
      visitorCountry: hotel?.visitor_country,
      visitorCountryIso: hotel?.visitor_country_iso,
      hotelCountry: hotel?.country,
      countryCodes,
      guestCountries,
    });
    const dialCode = locale.dialCode || '+91';
    if (!profileFormInitializedRef.current) {
      profileFormInitializedRef.current = true;
      setForm({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        email: profile.email || '',
        phone: normalizePhoneInputForCountry(profile.phone || '', dialCode),
        country_code: dialCode,
        address_line1: profile.address_line1 || '',
        city: profile.city || '',
        state: profile.state || '',
        country: locale.countryName || resolveCountryNameForInput(profile.country, guestCountries) || '',
        pincode: profile.pincode || '',
        new_password: '',
      });
      return;
    }
    setForm((f) => ({
      ...f,
      country_code: dialCode,
      country: locale.countryName || f.country,
      phone: normalizePhoneInputForCountry(profile.phone || f.phone, dialCode),
    }));
  }, [
    profile,
    hotel?.visitor_country,
    hotel?.visitor_country_iso,
    hotel?.country,
    countryTouched,
    phoneCountryCodeTouched,
    countryCodes,
    guestCountries,
  ]);

  const isDark = theme.mode === 'dark';
  const inputSx = buildInputSxMinimal(theme);
  const passwordNotSet = profile && profile.password_set === false;
  const showPasswordPrompt = (fromBooking || passwordNotSet) && passwordNotSet;
  const passwordRequired = passwordNotSet;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!profile?.id || !hotelId) return;
    const countryIso =
      selectedGuestCountryObj?.code ||
      resolveCountryIsoForInput(form.country, guestCountries);
    const payload = {
      first_name: (form.first_name || '').trim() || undefined,
      last_name: (form.last_name || '').trim() || undefined,
      email: (form.email || '').trim() || undefined,
      phone: (form.phone || '').trim() || undefined,
      country_code: (form.country_code || '').trim() || undefined,
      address_line1: (form.address_line1 || '').trim() || undefined,
      city: (form.city || '').trim() || undefined,
      state: (form.state || '').trim() || undefined,
      country: countryIso || undefined,
      pincode: (form.pincode || '').trim() || undefined,
    };
    if ((form.new_password || '').trim()) {
      payload.new_password = form.new_password.trim();
    } else if (passwordRequired) {
      setSaveError('Please set a password (at least 6 characters) to secure your account.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    updateGuest(hotelId, profile.id, payload)
      .then(() => {
        setSaveSuccess(true);
        setForm((f) => ({ ...f, new_password: '' }));
        return getGuestProfile(hotelId, profile.id);
      })
      .then((data) => setProfile(data))
      .catch((err) => {
        const data = err?.response?.data;
        let msg =
          data?.errors?.new_password?.[0] ||
          data?.errors?.country?.[0] ||
          data?.detail ||
          err?.message ||
          'Failed to update.';
        if (!msg && data?.errors && typeof data.errors === 'object') {
          const parts = Object.values(data.errors).flat().filter(Boolean);
          if (parts.length) msg = parts.join(' ');
        }
        setSaveError(typeof msg === 'string' ? msg : 'Failed to update.');
      })
      .finally(() => { setSaving(false); });
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: theme.bg, py: 4, px: 2 }}>
        <Box sx={{ maxWidth: 960, mx: 'auto' }}>
          <Paper elevation={0} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', p: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
              <Skeleton variant="circular" width={72} height={72} sx={{ flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Skeleton variant="text" width="70%" height={36} sx={{ mb: 1 }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Skeleton variant="text" width={48} height={40} sx={{ mx: 'auto' }} />
                  <Skeleton variant="text" width={90} height={20} />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Skeleton variant="text" width={80} height={32} sx={{ mx: 'auto' }} />
                  <Skeleton variant="text" width={70} height={20} />
                </Box>
              </Box>
            </Box>
          </Paper>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Skeleton variant="text" width="50%" height={28} sx={{ mb: 2 }} />
                  <Skeleton variant="text" width="90%" height={24} sx={{ mb: 1.5 }} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              <Skeleton variant="rounded" height={320} sx={{ borderRadius: 2 }} />
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  }

  if (error === 'not_logged_in') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: theme.bg, py: 8, px: 2, color: theme.textPrimary }}>
        <Box sx={{ maxWidth: 480, mx: 'auto', textAlign: 'center' }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              mx: 'auto',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${accent}20`,
              color: accent,
            }}
          >
            <PersonIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>My Profile</Typography>
          <Typography variant="body2" sx={{ color: theme.textSecondary, mb: 3 }}>
            Register or log in to edit your profile.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
            <Button
              component={Link}
              to={nexastayPath(basePath, hotelId)}
              variant="outlined"
              startIcon={<HomeIcon />}
              sx={{
                borderColor: theme.borderAccent,
                color: theme.textPrimary,
                '&:hover': { borderColor: accent, bgcolor: `${accent}12`, color: accent },
              }}
            >
              Back to home
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: theme.bg, py: 4, px: 2 }}>
        <Box sx={{ maxWidth: 640, mx: 'auto' }}>
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, bgcolor: theme.paper }}>{error || 'Profile not found.'}</Alert>
          <Button component={Link} to={nexastayPath(basePath, hotelId)} sx={{ color: accent }}>Back to home</Button>
        </Box>
      </Box>
    );
  }

  const addressParts = [
    profile.address_line1,
    profile.city,
    profile.state,
    resolveCountryNameForInput(profile.country, guestCountries) || profile.country,
    profile.pincode,
  ].filter(Boolean);
  const totalBookings = profile.total_bookings ?? 0;
  const totalSpent = profile.total_spent ?? 0;
  const currency = profile.currency || 'INR';
  const formatCurrency = (amount, curr) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: curr || 'INR', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.bg, pb: 6, color: theme.textPrimary }}>
      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2, sm: 3 }, pt: 2 }}>
        {/* Hero – same as My Booking */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2,
            overflow: 'hidden',
            background: isDark
              ? `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, ${alpha(accent, 0.04)} 50%, #2a2a31 100%)`
              : `linear-gradient(135deg, ${alpha(accent, 0.15)} 0%, ${alpha(accent, 0.05)} 50%, #fff 100%)`,
            border: `1px solid ${isDark ? alpha(accent, 0.2) : alpha(accent, 0.25)}`,
            boxShadow: `0 4px 20px ${alpha(accent, 0.15)}`,
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `linear-gradient(135deg, ${accent} 0%, #3ab896 100%)`,
                  boxShadow: `0 4px 16px ${alpha(accent, 0.4)}`,
                  border: `3px solid ${isDark ? alpha(accent, 0.3) : alpha(accent, 0.5)}`,
                }}
              >
                <PersonIcon sx={{ fontSize: 36, color: '#000' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#1a1a1a' }}>
                  {profile.full_name || 'Guest'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                    <ReceiptLongIcon sx={{ fontSize: 20, color: accent }} />
                    <Typography variant="h4" sx={{ fontWeight: 700, color: accent }}>{totalBookings}</Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666' }}>Total Bookings</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                    <PaidIcon sx={{ fontSize: 20, color: accent }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: accent }}>
                      {totalSpent > 0 ? formatCurrency(totalSpent, currency) : '—'}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666' }}>Total Spent</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          {/* Left: read-only profile details */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                border: `1px solid ${isDark ? '#333' : '#e0e0e0'}`,
                backgroundColor: isDark ? '#2a2a31' : theme.paper,
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isDark ? '#ddd' : '#333', display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PersonIcon sx={{ color: accent, fontSize: 22 }} />
                  Profile details
                </Typography>
                {profile.email ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <EmailIcon sx={{ fontSize: 20, color: accent }} />
                    <Typography variant="body2" sx={{ color: isDark ? '#ccc' : '#555' }}>{profile.email}</Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666', mb: 1.5 }}>Email —</Typography>
                )}
                {profile.phone ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <PhoneIcon sx={{ fontSize: 20, color: accent }} />
                    <Typography variant="body2" sx={{ color: isDark ? '#ccc' : '#555' }}>
                      {formatPhoneWithDialCode(profile.phone, profile.country_code)}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666', mb: 1.5 }}>Phone —</Typography>
                )}
                {addressParts.length > 0 ? (
                  <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${isDark ? '#444' : '#eee'}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <LocationOnIcon sx={{ fontSize: 20, color: accent, mt: 0.25 }} />
                      <Typography variant="body2" sx={{ color: isDark ? '#ccc' : '#555' }}>{addressParts.join(', ')}</Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${isDark ? '#444' : '#eee'}` }}>
                    <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666' }}>Address —</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Button
              component={Link}
              to={nexastayPath(basePath, hotelId)}
              variant="outlined"
              startIcon={<HomeIcon />}
              fullWidth
              sx={{
                mt: 2,
                py: 1.5,
                borderRadius: 2,
                borderColor: theme.borderAccent,
                color: theme.textPrimary,
                fontWeight: 600,
                '&:hover': { borderColor: accent, bgcolor: `${accent}12`, color: accent },
              }}
            >
              Back to home
            </Button>
          </Grid>

          {/* Right: editable form */}
          <Grid item xs={12} md={8}>
            <Paper
              component="form"
              onSubmit={handleSubmit}
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                border: `1px solid ${isDark ? '#333' : '#e0e0e0'}`,
                bgcolor: isDark ? '#2a2a31' : theme.paper,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isDark ? '#ddd' : '#333', mb: 2 }}>
                Edit profile
              </Typography>

              {showPasswordPrompt && (
                <Alert
                  severity="info"
                  icon={<LockIcon />}
                  sx={{ mb: 2, borderRadius: 2, bgcolor: alpha(accent, 0.08), border: `1px solid ${alpha(accent, 0.3)}` }}
                >
                  Set a password below to secure your account (required).
                </Alert>
              )}

              {saveSuccess && (
                <Alert severity="success" onClose={() => setSaveSuccess(false)} sx={{ mb: 2, borderRadius: 2 }}>
                  Profile updated successfully.
                </Alert>
              )}
              {saveError && (
                <Alert severity="error" onClose={() => setSaveError(null)} sx={{ mb: 2, borderRadius: 2 }}>{saveError}</Alert>
              )}

              <Stack spacing={2}>
                <Stack direction="row" spacing={2}>
                  <TextField
                    fullWidth
                    label="First name"
                    value={form.first_name}
                    onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                    sx={inputSx}
                  />
                  <TextField
                    fullWidth
                    label="Last name"
                    value={form.last_name}
                    onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                    sx={inputSx}
                  />
                </Stack>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  sx={inputSx}
                />
                <Autocomplete
                  options={guestCountries}
                  getOptionLabel={(option) => option?.name || ''}
                  filterOptions={countryFilterOptions}
                  value={selectedGuestCountryObj}
                  isOptionEqualToValue={(opt, val) => opt?.code === val?.code}
                  onChange={(event, newValue) => {
                    const countryName = newValue?.name || '';
                    setCountryTouched(true);
                    setPhoneCountryCodeTouched(false);
                    const dialCode = resolveDialCodeForCountryInput(
                      newValue?.code || countryName,
                      countryCodes,
                      guestCountries
                    );
                    setForm((f) => ({
                      ...f,
                      country: countryName,
                      ...(dialCode
                        ? {
                            country_code: dialCode,
                            phone: normalizePhoneInputForCountry(f.phone, dialCode),
                          }
                        : {}),
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Country"
                      helperText="Phone country code follows the selected country"
                      sx={inputSx}
                    />
                  )}
                />
                <TextField
                  fullWidth
                  label="Phone"
                  value={form.phone}
                  onChange={(e) => {
                    const dialCode = form.country_code || selectedPhoneCountryCode?.dialCode || '+91';
                    setForm((f) => ({
                      ...f,
                      phone: normalizePhoneInputForCountry(e.target.value, dialCode),
                    }));
                  }}
                  helperText={`Enter ${formatPhoneLengthLabel(form.country_code || selectedPhoneCountryCode?.dialCode || '+91')}`}
                  inputProps={{ inputMode: 'tel' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          ref={phoneCountryCodeAnchorRef}
                          onClick={() => setPhoneCountryCodeMenuOpen(true)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            gap: 0.5,
                            px: 0.75,
                            py: 0.5,
                            borderRadius: 1,
                            mr: 0.5,
                            '&:hover': {
                              bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                            },
                          }}
                        >
                          <Typography component="span" sx={{ fontSize: '1.1rem' }}>
                            {selectedPhoneCountryCode?.flag || '🇮🇳'}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{ fontWeight: 600, fontSize: '0.875rem', color: theme.textPrimary }}
                          >
                            {selectedPhoneCountryCode?.dialCode || form.country_code || '+91'}
                          </Typography>
                          <Typography component="span" sx={{ fontSize: '0.7rem', color: theme.textSecondary }}>
                            ▼
                          </Typography>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
                <Menu
                  anchorEl={phoneCountryCodeAnchorRef.current}
                  open={phoneCountryCodeMenuOpen}
                  onClose={() => {
                    setPhoneCountryCodeMenuOpen(false);
                    setPhoneCountryCodeSearch('');
                  }}
                  PaperProps={{ sx: { maxHeight: 400, width: 300 } }}
                >
                  <Box sx={{ p: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search country code..."
                      value={phoneCountryCodeSearch}
                      onChange={(e) => setPhoneCountryCodeSearch(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                  </Box>
                  {countryCodes
                    .filter((c) => {
                      if (!phoneCountryCodeSearch) return true;
                      const q = phoneCountryCodeSearch.toLowerCase();
                      return (
                        c.name.toLowerCase().includes(q) ||
                        c.dialCode.includes(q) ||
                        c.code.toLowerCase().includes(q)
                      );
                    })
                    .map((c) => (
                      <MenuItem
                        key={c.code}
                        onClick={() => {
                          setPhoneCountryCodeTouched(true);
                          setCountryTouched(true);
                          setForm((f) => ({
                            ...f,
                            country_code: c.dialCode,
                            country: c.name,
                            phone: normalizePhoneInputForCountry(f.phone, c.dialCode),
                          }));
                          setPhoneCountryCodeMenuOpen(false);
                          setPhoneCountryCodeSearch('');
                        }}
                      >
                        <Typography component="span" sx={{ mr: 1 }}>{c.flag}</Typography>
                        {c.name} ({c.dialCode})
                      </MenuItem>
                    ))}
                </Menu>
                <TextField
                  fullWidth
                  label="Address"
                  value={form.address_line1}
                  onChange={(e) => setForm((f) => ({ ...f, address_line1: e.target.value }))}
                  sx={inputSx}
                />
                <Stack direction="row" spacing={2}>
                  <TextField
                    fullWidth
                    label="City"
                    value={form.city}
                    onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                    sx={inputSx}
                  />
                  <TextField
                    fullWidth
                    label="State"
                    value={form.state}
                    onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                    sx={inputSx}
                  />
                </Stack>
                <Stack direction="row" spacing={2}>
                  <TextField
                    fullWidth
                    label="Pincode / ZIP"
                    value={form.pincode}
                    onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value }))}
                    sx={inputSx}
                  />
                </Stack>
                <TextField
                  fullWidth
                  label={passwordNotSet ? 'Password (min 6 characters) *' : 'New password (leave blank to keep current)'}
                  type="password"
                  value={form.new_password}
                  onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))}
                  placeholder={passwordNotSet ? 'Required' : ''}
                  required={passwordRequired}
                  sx={inputSx}
                />
                <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                    startIcon={<SaveIcon />}
                    sx={{
                      bgcolor: accent,
                      color: theme.mode === 'dark' ? '#121212' : '#000',
                      fontWeight: 600,
                      '&:hover': { bgcolor: accent },
                    }}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                  <Button
                    component={Link}
                    to={nexastayPath(basePath, hotelId, 'bookings')}
                    variant="outlined"
                    sx={{
                      borderColor: theme.borderAccent,
                      color: theme.textPrimary,
                      '&:hover': { borderColor: accent, bgcolor: `${accent}12`, color: accent },
                    }}
                  >
                    My Booking
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
